import Vue from "vue";
import VueRouter, { NavigationFailureType, Route, RouteConfig, isNavigationFailure } from "vue-router";
import { rootPathGuard, multipleGuards } from "./guards/index";
import { i18n } from "@/i18n";
import { RouteInfo } from "./types";
import { AdministrationRoute } from "./administration.route";
import { LoginRoute } from "./login.route";
import { DashboardRoute } from "./dashboard.route";
import { TrackingSaleProcessesRoute } from "./tracking-sale-processes.route";
import { CoverageSearchRoute } from "./coverage-search.route";
import { SaleRoute } from "./sale.route";
import { TrackingSalesRoute } from "./tracking-sales.route";
import { ApprovalDetailRoute, ApprovalsRoute } from "./approvals.route";
import { CustomersRoute } from "./customers.route";
import { Customer360Route } from "./customer/customer-360.route";
import { CustomerProductTransitionRoute } from "./customer/customer-product-transition.route";
import {
	CustomerProductOnlyMobilesTransitionRoute,
	CustomerProductSharedMobilesTransitionRoute
} from "./customer/customer-product-multiple-transition.route";
import { CustomerProductModificationRoute } from "./customer/customer-product-modification.route";
import { CustomerProductTerminationRoute } from "./customer/customer-product-termination.route";
import { CustomerNewExtrasRoute } from "./customer/customer-new-extras.route";
import { CustomerProductChangeOfAddressRoute } from "./customer/customer-product-change-of-address.route";
import { CustomerProductChangeOfAddress2ndPhaseRoute } from "./customer/customer-product-change-of-address-2nd-phase.route";
import { CustomerHandoverRoute } from "./customer/customer-handover.route";
import { RetailRoute } from "./retail.route";
import { ProductsRoute } from "./products.route";
import { TrackingCustomersAggrupationsRoute } from "./tracking-customers-aggrupations.route";
import { CustomerProductUsageDataRoute } from "./customer/customer-product-usage-data.route";
import { GenericSaleRoute } from "./generic-sale.route";

Vue.use(VueRouter);

const routes: RouteInfo[] = [
	LoginRoute,
	DashboardRoute,
	SaleRoute,
	GenericSaleRoute,
	TrackingSaleProcessesRoute,
	TrackingSalesRoute,
	CoverageSearchRoute,
	ApprovalsRoute,
	ApprovalDetailRoute,
	CustomersRoute,
	TrackingCustomersAggrupationsRoute,
	Customer360Route,
	CustomerProductTransitionRoute,
	CustomerProductOnlyMobilesTransitionRoute,
	CustomerProductSharedMobilesTransitionRoute,
	CustomerProductModificationRoute,
	CustomerProductTerminationRoute,
	CustomerNewExtrasRoute,
	CustomerProductChangeOfAddressRoute,
	CustomerProductChangeOfAddress2ndPhaseRoute,
	CustomerHandoverRoute,
	CustomerProductUsageDataRoute,
	ProductsRoute,
	RetailRoute,
	AdministrationRoute,
	{
		id: "wildcard",
		path: "*",
		guards: [rootPathGuard]
	}
];

function mapRoutes(routes: RouteInfo[]): RouteConfig[] {
	return routes.map((route) => {
		const { id, path, component, title, guards, children, props, redirect } = route;
		const name = children && children.length ? undefined : id;
		const beforeEnter = guards && guards.length ? multipleGuards(guards) : undefined;

		return {
			name,
			path,
			component,
			beforeEnter,
			props,
			meta: {
				id,
				title,
				childrenIds: children ? children.map((child) => child.id) : undefined
			},
			children: children ? mapRoutes(children) : undefined,
			redirect
		};
	});
}

function filterRoutes(...ids: string[]): RouteInfo[] {
	return routes.reduce((acc: RouteInfo[], route: RouteInfo) => {
		const { id, children = [] } = route;
		if (ids.includes(id)) {
			acc.push(route);
			return acc;
		}

		if (children.length) {
			const childRoutes = children.filter((child) => ids.includes(child.id));
			acc.push(...childRoutes);
		}
		return acc;
	}, []);
}

function findRoute({ id }: { id: string }): RouteInfo | undefined {
	const route = routes.find((route) => route.id === id);
	if (route) return route;

	const routeWithChildren = routes.find((route) => {
		const { children = [] } = route;

		return children.find((childRoute) => childRoute.id === id);
	});
	if (routeWithChildren) {
		const { path, children = [] } = routeWithChildren;
		const childRoute = children.find((childRoute) => childRoute.id === id);

		if (childRoute) {
			return {
				...childRoute,
				path: `${path}/${childRoute.path}`,
				uriPath: childRoute.path
			};
		}
	}
}

const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = async function push(location) {
	try {
		return await (originalPush.call(this, location) as unknown as Promise<any>);
	} catch (err) {
		if (isNavigationFailure(err, NavigationFailureType.redirected)) {
			return err;
		}

		if (isNavigationFailure(err, NavigationFailureType.duplicated)) {
			return err;
		}

		if (isNavigationFailure(err, NavigationFailureType.aborted)) {
			return err;
		}

		if (isNavigationFailure(err, NavigationFailureType.cancelled)) {
			return err;
		}

		return Promise.reject(err);
	}
};

const router = new VueRouter({
	mode: "history",
	routes: mapRoutes(routes)
});

router.beforeEach((to: Route, from: Route, next: () => void) => {
	window.scrollTo(0, 0);
	window.document.title = to.meta && to.meta.title ? `${i18n.t(to.meta.title)} | ARS` : "ARS";
	next();
});

export { router, routes, findRoute, filterRoutes };
